import { useState, useMemo } from "react";
import { format, subDays, subMonths, subYears } from "date-fns";
import type { LabResult, LabReading, ChartType, TimeRange, ChartConfig } from "../types";
import { parseRefRange } from "../utils";

export interface UseTrendChartProps {
  result: LabResult;
  selectedReadingId?: string;
}

const getCutoffDate = (range: TimeRange): Date | null => {
  const now = new Date();
  if (range === "week") return subDays(now, 7);
  if (range === "month") return subMonths(now, 1);
  if (range === "year") return subYears(now, 1);
  return null;
};

export const useTrendChart = ({ result, selectedReadingId }: UseTrendChartProps) => {
  const [chartType, setChartType] = useState<ChartType>("line");
  const [timeRange, setTimeRange] = useState<TimeRange>("all");

  const refRange = useMemo(() => {
    const refRangeStr = result.readings[0]?.refRange;
    return refRangeStr ? parseRefRange(refRangeStr) : null;
  }, [result.readings]);

  const filteredData = useMemo(() => {
    const cutoff = getCutoffDate(timeRange);
    const sorted = [...result.readings].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const filtered = cutoff ? sorted.filter((r) => new Date(r.date) >= cutoff) : sorted;

    return filtered.map((r) => ({
      ...r,
      dateLabel: format(new Date(r.date), "MMM d"),
      isSelected: r.id === selectedReadingId,
    }));
  }, [result.readings, timeRange, selectedReadingId]);

  const chartConfig = useMemo((): ChartConfig => {
    const allValues = filteredData.map((d) => d.value);
    const minVal = Math.min(...allValues);
    const maxVal = Math.max(...allValues);
    const padding = (maxVal - minVal) * 0.3 || 1;

    return {
      type: chartType,
      timeRange,
      refRange,
      filteredData,
      yMin: Math.max(0, Math.floor(minVal - padding)),
      yMax: Math.ceil(maxVal + padding),
      hasData: filteredData.length > 0,
    };
  }, [chartType, timeRange, refRange, filteredData]);

  const handleDataPointClick = (data: unknown) => {
    const chartData = data as { activePayload?: Array<{ payload?: LabReading }> } | undefined;
    return chartData?.activePayload?.[0]?.payload;
  };

  return {
    chartType,
    setChartType,
    timeRange,
    setTimeRange,
    chartConfig,
    handleDataPointClick,
  };
};

export type UseTrendChartReturn = ReturnType<typeof useTrendChart>;
